import {ChangeDetectionStrategy, Component, computed, inject, Signal} from '@angular/core';
import {CommonModule} from '@angular/common';
import {ActivatedRoute, Params, RouterLink} from '@angular/router';
import {HttpClient} from '@angular/common/http';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {map, Observable} from 'rxjs';

import {DrawingModel, EventRankingModel, GameModel, GroupRankingModel, TournamentModel} from 'shared/models';
import {HttpLoaderService, LoadResult} from 'shared/services';
import {ViewUi} from '../view-ui/view-ui';

interface DataShape {
  drawings: {list: DrawingModel[]};
  games: {list: GameModel[]};
  groupsRankings: {list: GroupRankingModel[]};
  eventsRankings: {list: EventRankingModel[]};
}

// Parametros de las llamadas
interface ApiParams {
  drawings: {tournamentId: number};
  games: {tournamentId: number};
  groupsRankings: {tournamentId: number};
  eventsRankings: {tournamentId: number};
}

@Component({
  // selector: 'tournaments-view',
  standalone: true,
  imports: [CommonModule, RouterLink, MatProgressSpinner, ViewUi],
  templateUrl: './view.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class View {
  private http: HttpClient = inject(HttpClient);
  private loaderService: HttpLoaderService = inject(HttpLoaderService);

  protected model!: TournamentModel;

  private loader: LoadResult<DataShape, ApiParams> = this.loaderService.createLoader<DataShape, ApiParams>({
    drawings: (params: ApiParams['drawings']): Observable<DataShape['drawings']> => {
      return this.http.get(`tournaments/${params.tournamentId}/drawings`).pipe(
        map((data: Params): DataShape['drawings'] => {
          const list: DrawingModel[] = data['data'].map((item: Params): DrawingModel => {
            return DrawingModel.getInstance(item);
          });

          // const paginator: Paginator = {
          //   total: data.total,
          //   perPage: data.perPage,
          //   currentPage: data.currentPage,
          //   lastPage: data.lastPage,
          //   // from: data.from,
          //   // to: data.to
          // };

          // return {list, paginator};
          return {list};
        })
      );
    },
    games: (params: ApiParams['games']): Observable<DataShape['games']> => {
      return this.http.get(`tournaments/${params.tournamentId}/games`).pipe(
        map((data: Params): DataShape['games'] => {
          const list: GameModel[] = data['data'].map((item: Params): GameModel => {
            return GameModel.getInstance(item);
          });

          // const paginator: Paginator = {
          //   total: data.total,
          //   perPage: data.perPage,
          //   currentPage: data.currentPage,
          //   lastPage: data.lastPage,
          //   // from: data.from,
          //   // to: data.to
          // };

          // return {list, paginator};
          return {list};
        })
      );
    },
    groupsRankings: (params: ApiParams['groupsRankings']): Observable<DataShape['groupsRankings']> => {
      return this.http.get(`tournaments/${params.tournamentId}/groups-rankings`).pipe(
        map((data: Params): DataShape['groupsRankings'] => {
          const list: GroupRankingModel[] = data['data'].map((item: Params): GroupRankingModel => {
            return GroupRankingModel.getInstance(item);
          });

          // const paginator: Paginator = {
          //   total: data.total,
          //   perPage: data.perPage,
          //   currentPage: data.currentPage,
          //   lastPage: data.lastPage,
          //   // from: data.from,
          //   // to: data.to
          // };

          // return {list, paginator};
          return {list};
        })
      );
    },
    eventsRankings: (params: ApiParams['eventsRankings']): Observable<DataShape['eventsRankings']> => {
      return this.http.get(`tournaments/${params.tournamentId}/events-rankings`).pipe(
        map((data: Params): DataShape['eventsRankings'] => {
          const list: EventRankingModel[] = data['data'].map((item: Params): EventRankingModel => {
            return EventRankingModel.getInstance(item);
          });

          // const paginator: Paginator = {
          //   total: data.total,
          //   perPage: data.perPage,
          //   currentPage: data.currentPage,
          //   lastPage: data.lastPage,
          //   // from: data.from,
          //   // to: data.to
          // };

          // return {list, paginator};
          return {list};
        })
      );
    },
  });

  protected hasAttemptedLoad: Signal<boolean> = this.loader.hasAttemptedLoad;
  protected isLoading: Signal<boolean> = this.loader.isLoading;
  protected error: Signal<string | null> = this.loader.error;
  // Deriva los signals individuales
  protected drawings: Signal<DrawingModel[]> = computed((): DrawingModel[] => {
    return this.loader.data()?.drawings?.list || [];
  });
  protected games: Signal<GameModel[]> = computed((): GameModel[] => {
    return this.loader.data()?.games?.list || [];
  });
  protected groupsRankings: Signal<GroupRankingModel[]> = computed((): GroupRankingModel[] => {
    return this.loader.data()?.groupsRankings?.list || [];
  });
  protected eventsRankings: Signal<EventRankingModel[]> = computed((): EventRankingModel[] => {
    return this.loader.data()?.eventsRankings?.list || [];
  });

  constructor(
    private route: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    this.model = this.route.snapshot.data['model'];

    this.loadData();
  }

  loadData(): void {
    const params: ApiParams = {
      drawings: {tournamentId: this.model.id},
      games: {tournamentId: this.model.id},
      groupsRankings: {tournamentId: this.model.id},
      eventsRankings: {tournamentId: this.model.id},
    };
    this.loader.trigger(params);
  }
}
